/* ============================================================
   TEE BOX — PWA INSTALL + SERVICE WORKER

   Two jobs, both of which have to happen before React mounts:

     1. Register the service worker. Without a fetch handler
        Chromium won't consider the site installable at all.
     2. Catch `beforeinstallprompt`. The browser fires it once,
        early — typically before the v2 chunk has even been
        requested — and the event is only usable if you called
        `preventDefault()` on it synchronously in that handler.
        A React component mounting later would miss it entirely.

   So this is a module-level store, initialised from `index.tsx`,
   that components subscribe to. `useInstallPrompt()` in
   `@/hooks/useInstallPrompt` is the React face of it.

   NOT ON NATIVE. The app also runs inside a React Native WebView,
   where it already is an installed app — offering to "install" it
   there is nonsense, and a service worker inside the WebView adds
   a caching layer the native shell doesn't expect. Both jobs
   no-op when `isNativeWebView()`.
   ============================================================ */

import { isNativeWebView } from './nativeBridge';

/** The slice of `BeforeInstallPromptEvent` we actually use. It is
 *  Chromium-only and absent from lib.dom. */
type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

export type InstallState = {
  /** The browser has offered a prompt we haven't spent yet. */
  canInstall: boolean;
  /** Running from the dock / Start menu rather than a browser tab. */
  isInstalled: boolean;
  /** A newer service worker is waiting to take over. */
  updateReady: boolean;
};

let deferredPrompt: InstallPromptEvent | null = null;
let waitingWorker: ServiceWorker | null = null;

const listeners = new Set<(state: InstallState) => void>();

/**
 * Standalone detection.
 *
 * `display-mode: standalone` is the spec answer and covers
 * Chromium desktop, Android and iOS 16.4+. `navigator.standalone`
 * is the legacy iOS Safari flag, still the only signal on older
 * iOS. Either one means we're already installed.
 */
export const isStandalone = (): boolean => {
  try {
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      window.matchMedia('(display-mode: window-controls-overlay)').matches ||
      (window.navigator as unknown as { standalone?: boolean }).standalone === true
    );
  } catch {
    return false;
  }
};

/**
 * Whether this is a Safari that can "Add to Dock".
 *
 * Safari has no `beforeinstallprompt` and no programmatic install
 * of any kind — the only route is the member choosing **File →
 * Add to Dock** themselves. So the affordance we show there is
 * instructional, and this is the gate on showing it at all: a card
 * naming a menu item the browser doesn't have is worse than no
 * card.
 *
 * THE UA SNIFFING IS THE POINT, not laziness. There is no feature
 * to detect: `window.showAddToDock` doesn't exist, and the menu
 * item leaves no trace in the DOM. Version and platform are the
 * only signals available.
 *
 * Three traps this navigates:
 *
 *   1. Every Chromium browser on macOS carries "Safari" in its UA,
 *      so Safari must be identified by exclusion first.
 *   2. Add to Dock is macOS-only. iPadOS in its default desktop
 *      mode reports "Macintosh" verbatim; `maxTouchPoints` is what
 *      separates a real Mac from it. (iPad has Add to Home Screen,
 *      a different flow with a different menu path.)
 *   3. It shipped in Safari 17 / macOS Sonoma. Earlier Safaris
 *      have no equivalent at all.
 */
export const canAddToDock = (): boolean => {
  try {
    const ua = navigator.userAgent;
    if (/Chrome|Chromium|CriOS|FxiOS|Firefox|Edg|OPR|Brave/i.test(ua))
      return false;
    if (!/Safari/i.test(ua)) return false;
    if (!/Macintosh/i.test(ua)) return false;
    if (navigator.maxTouchPoints > 1) return false;

    const version = /Version\/(\d+)/.exec(ua);
    return version !== null && Number(version[1]) >= 17;
  } catch {
    return false;
  }
};

export const getInstallState = (): InstallState => ({
  canInstall: deferredPrompt !== null,
  isInstalled: isStandalone(),
  updateReady: waitingWorker !== null,
});

const emit = () => {
  const state = getInstallState();
  listeners.forEach((listener) => listener(state));
};

/** Subscribe to install-state changes. Returns the unsubscribe. */
export const subscribeToInstallState = (
  listener: (state: InstallState) => void,
): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/**
 * Show the browser's install dialog.
 *
 * Must be called from a user gesture — Chromium rejects a
 * `prompt()` that isn't. Resolves to the member's answer, or
 * `'unavailable'` if there was no stored event (Safari, Firefox,
 * or a prompt already spent).
 *
 * The event is single-use either way: a dismissed prompt cannot
 * be re-shown with the same event, so it is dropped on both
 * outcomes and `canInstall` goes false. Chromium re-fires
 * `beforeinstallprompt` on a later visit if the member didn't
 * install, which is what restores the affordance.
 */
export const promptInstall = async (): Promise<
  'accepted' | 'dismissed' | 'unavailable'
> => {
  const event = deferredPrompt;
  if (!event) return 'unavailable';

  deferredPrompt = null;
  emit();

  try {
    await event.prompt();
    const { outcome } = await event.userChoice;
    return outcome;
  } catch {
    return 'unavailable';
  }
};

/** Hand over to the waiting worker and reload onto the new build. */
export const applyUpdate = (): void => {
  if (!waitingWorker) return;
  waitingWorker.postMessage({ type: 'SKIP_WAITING' });
  /* `controllerchange` fires once the new worker claims this
     page; reloading any earlier would just re-run the old build. */
};

const trackWaiting = (registration: ServiceWorkerRegistration) => {
  const promote = (worker: ServiceWorker | null) => {
    if (!worker) return;
    /* `installed` + an existing controller means this is an
       update, not a first install. A first install has nothing
       to tell the member about. */
    if (worker.state === 'installed' && navigator.serviceWorker.controller) {
      waitingWorker = worker;
      emit();
    }
  };

  promote(registration.waiting);

  registration.addEventListener('updatefound', () => {
    const installing = registration.installing;
    if (!installing) return;
    installing.addEventListener('statechange', () => promote(installing));
  });
};

let initialised = false;

/**
 * Wire everything up. Safe to call more than once; idempotent.
 * Called from `index.tsx` before the first render.
 */
export const initPwa = (): void => {
  if (initialised) return;
  initialised = true;

  if (typeof window === 'undefined') return;
  if (isNativeWebView()) return;

  window.addEventListener('beforeinstallprompt', (event) => {
    /* Suppress Chromium's own mini-infobar so the in-app
       affordance is the only thing the member sees. */
    event.preventDefault();
    deferredPrompt = event as InstallPromptEvent;
    emit();
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    emit();
  });

  /* A window launched from the dock reports standalone only after
     the media query settles; re-emit so anything mounted early
     picks up the change. */
  try {
    window
      .matchMedia('(display-mode: standalone)')
      .addEventListener('change', emit);
  } catch {
    /* Older Safari has no addEventListener on MediaQueryList. */
  }

  if (!('serviceWorker' in navigator)) return;

  /* DEV: no worker. A cached shell in front of Vite's HMR turns
     every edit into a stale-asset hunt. Any worker registered by
     a previous production visit to the same origin is torn down
     rather than left to serve stale bytes on localhost. */
  if (import.meta.env.DEV) {
    navigator.serviceWorker
      .getRegistrations()
      .then((registrations) =>
        registrations.forEach((registration) => registration.unregister()),
      )
      .catch(() => undefined);
    return;
  }

  /* After `load`, so worker registration never competes with the
     first paint for bandwidth. */
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .then(trackWaiting)
      .catch(() => undefined);
  });

  /* Whether this page load was already under a worker's control.
     `activate` calls `clients.claim()`, which fires
     `controllerchange` on a FIRST install too — reloading there
     would bounce every member's first visit for no reason. Only
     a handover from one worker to another is an update. */
  const hadController = navigator.serviceWorker.controller !== null;

  let reloading = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadController || reloading) return;
    reloading = true;
    window.location.reload();
  });
};
